import type { Board } from '@/stores/canvasStore';

/** The bits of an `items` row board search reads (a folder card is a `board_link` row) */
export interface BoardSearchRow {
  id: string;
  board_id: string | null;
  item_type: string;
  content: unknown;
}

/** A board whose name matches the search, and where its folder card sits */
export interface BoardSearchResult {
  board: Board;
  /** Names of the boards above it, outermost first: ["Home", "Projects"] */
  path: string[];
  /** The folder card that opens this board; null for the home board */
  folder: { id: string; board_id: string } | null;
  /** Folder color as stored on the card, else the board's own */
  color: string | undefined;
}

export const BOARD_RESULT_LIMIT = 5;

/** How well a board name answers the query; 0 means it does not match */
export function scoreBoardName(name: string, query: string): number {
  const n = name.toLowerCase().trim();
  const q = query.toLowerCase().trim();
  if (!n || !q) return 0;
  if (n === q) return 100;
  if (n.startsWith(q)) return 60;
  // "recruit" finds "Seun's Recruiting"
  const words = n.split(/[^\p{L}\p{N}]+/u).filter(Boolean);
  if (words.some((w) => w.startsWith(q))) return 40;
  if (n.includes(q)) return 20;
  // "mindfull ui" finds "UI for Mindfull"
  const queryWords = q.split(/\s+/).filter(Boolean);
  if (queryWords.length > 1 && queryWords.every((w) => n.includes(w))) return 10;
  return 0;
}

function linkedBoardId(row: BoardSearchRow): string | null {
  if (row.item_type !== 'board_link' || !row.content || typeof row.content !== 'object') return null;
  const id = (row.content as Record<string, unknown>).linked_board_id;
  return typeof id === 'string' ? id : null;
}

function ancestorNames(board: Board, byId: Map<string, Board>): string[] {
  const names: string[] = [];
  const seen = new Set([board.id]);
  let parent = board.parent_id ? byId.get(board.parent_id) : undefined;
  while (parent && !seen.has(parent.id)) {
    seen.add(parent.id);
    names.unshift(parent.name);
    parent = parent.parent_id ? byId.get(parent.parent_id) : undefined;
  }
  return names;
}

/**
 * Boards whose names match the query, best first.
 *
 * Only the owner's boards count: RLS also returns other people's boards that
 * have a public share link. A sub-board needs a folder card, as in
 * `getVisibleBoards`, so a board left behind by a half-finished delete stays out.
 */
export function searchBoards(
  query: string,
  boards: Board[],
  rows: BoardSearchRow[],
  ownerId: string | undefined,
  limit = BOARD_RESULT_LIMIT,
): BoardSearchResult[] {
  if (!query.trim()) return [];

  const byId = new Map(boards.map((b) => [b.id, b]));

  // Folder card per board it opens, preferring the one on the board's parent
  const folders = new Map<string, { id: string; board_id: string; color: unknown }>();
  for (const row of rows) {
    const linked = linkedBoardId(row);
    if (!linked || !row.board_id) continue;
    const onParent = row.board_id === byId.get(linked)?.parent_id;
    if (folders.has(linked) && !onParent) continue;
    const color = (row.content as Record<string, unknown>).color;
    folders.set(linked, { id: row.id, board_id: row.board_id, color });
  }

  return boards
    .filter((b) => !ownerId || b.user_id === ownerId)
    .filter((b) => b.parent_id === null || folders.has(b.id))
    .map((board) => ({ board, score: scoreBoardName(board.name, query) }))
    .filter((s) => s.score > 0)
    .sort(
      (a, b) =>
        b.score - a.score ||
        a.board.name.length - b.board.name.length ||
        (b.board.updated_at ?? '').localeCompare(a.board.updated_at ?? ''),
    )
    .slice(0, limit)
    .map(({ board }) => {
      const folder = folders.get(board.id);
      return {
        board,
        path: ancestorNames(board, byId),
        folder: folder ? { id: folder.id, board_id: folder.board_id } : null,
        color: typeof folder?.color === 'string' ? folder.color : board.icon_color,
      };
    });
}
